import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./AuthContext";
import { I18nProvider } from "./i18n";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ForcePasswordChange from "./pages/ForcePasswordChange";
import Dashboard from "./pages/Dashboard";
import Apartments from "./pages/Apartments";
import ApartmentDetail from "./pages/ApartmentDetail";
import Bookings from "./pages/Bookings";
import Cleanings from "./pages/Cleanings";
import Maintenance from "./pages/Maintenance";
import Payments from "./pages/Payments";
import Reports from "./pages/Reports";
import Calendar from "./pages/Calendar";
import Guests from "./pages/Guests";
import Owners from "./pages/Owners";
import OwnerPortal from "./pages/OwnerPortal";
import OwnerStatements from "./pages/OwnerStatements";
import Accounting from "./pages/Accounting";
import Executive from "./pages/Executive";
import Settings from "./pages/Settings";
import UsersAdmin from "./pages/Users";
import AuditLog from "./pages/AuditLog";
import FinancialEngine from "./pages/FinancialEngine";
import ReportCenter from "./pages/ReportCenter";
import CommunicationCenter from "./pages/CommunicationCenter";
import DailyBrief from "./pages/DailyBrief";
import ReleaseDashboard from "./pages/ReleaseDashboard";
import QRCenter from "./pages/QRCenter";
import GuestGuide from "./pages/GuestGuide";import LocalGuide from "./pages/LocalGuide";
import CheckinPublic from "./pages/CheckinPublic";
import GuestPortal from "./pages/GuestPortal";
import TaxPage from "./pages/TaxPage";
import CityTaxManager from "./pages/CityTaxManager";
import Pipeline from "./pages/Pipeline";
import Revenue from "./pages/Revenue";
import Today from "./pages/Today";
import Academy from "./pages/Academy";

const SECTION_PATH = {
  dashboard: "/", apartments: "/apartments", calendar: "/calendar", bookings: "/bookings",
  guests: "/guests", cleanings: "/cleanings", maintenance: "/maintenance", payments: "/payments",
  owners: "/owners", reports: "/reports", users: "/users", settings: "/settings", statements: "/statements", accounting: "/accounting", finance: "/report-center", communication: "/communication", contracts: "/financial-engine", executive: "/executive", audit: "/audit", release: "/release", qr: "/qr-center", owner_portal: "/owner-portal", pipeline: "/pipeline", academy: "/academy",
};

function Protected({ children, section }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="flex min-h-screen items-center justify-center text-slate-400">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  const sections = user.sections || [];
  if (section && !sections.includes(section)) {
    const first = sections[0] ? SECTION_PATH[sections[0]] : "/";
    return <Navigate to={first || "/"} replace />;
  }
  return <Layout>{children}</Layout>;
}

function LoginRoute() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function HomeRouter() {
  const { user } = useAuth();
  const isExec = (user?.sections || []).includes("executive");
  return isExec ? <Executive /> : <Dashboard />;
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginRoute />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/g/:code" element={<GuestGuide />} />
              <Route path="/p/:code" element={<GuestPortal />} />
              <Route path="/tax/:code" element={<TaxPage />} />
              <Route path="/checkin/:code" element={<CheckinPublic />} />
              <Route path="/" element={<Protected><Today /></Protected>} />
              <Route path="/dashboard" element={<Protected section="dashboard"><HomeRouter /></Protected>} />
              <Route path="/apartments" element={<Protected section="apartments"><Apartments /></Protected>} />
              <Route path="/apartments/:id" element={<Protected section="apartments"><ApartmentDetail /></Protected>} />
              <Route path="/calendar" element={<Protected section="calendar"><Calendar /></Protected>} />
              <Route path="/bookings" element={<Protected section="bookings"><Bookings /></Protected>} />
              <Route path="/guests" element={<Protected section="guests"><Guests /></Protected>} />
              <Route path="/cleanings" element={<Protected section="cleanings"><Cleanings /></Protected>} />
              <Route path="/maintenance" element={<Protected section="maintenance"><Maintenance /></Protected>} />
              <Route path="/payments" element={<Protected section="payments"><Payments /></Protected>} />
              <Route path="/revenue" element={<Protected section="revenue"><Revenue /></Protected>} />
              <Route path="/owners" element={<Protected section="owners"><Owners /></Protected>} />
              <Route path="/statements" element={<Protected section="statements"><OwnerStatements /></Protected>} />
              <Route path="/accounting" element={<Protected section="accounting"><Accounting /></Protected>} />
              <Route path="/city-tax" element={<Protected section="city_tax"><CityTaxManager /></Protected>} />
              <Route path="/audit" element={<Protected section="audit"><AuditLog /></Protected>} />
              <Route path="/financial-engine" element={<Protected section="contracts"><FinancialEngine /></Protected>} />
              <Route path="/report-center" element={<Protected section="finance"><ReportCenter /></Protected>} />
              <Route path="/communication" element={<Protected section="communication"><CommunicationCenter /></Protected>} />
              <Route path="/daily-brief" element={<Protected section="communication"><DailyBrief /></Protected>} />
              <Route path="/executive" element={<Protected section="executive"><Executive /></Protected>} />
              <Route path="/release" element={<Protected section="release"><ReleaseDashboard /></Protected>} />
              <Route path="/qr-center" element={<Protected section="qr"><QRCenter /></Protected>} />
              <Route path="/local-guide" element={<Protected section="qr"><LocalGuide /></Protected>} />
              <Route path="/pipeline" element={<Protected section="pipeline"><Pipeline /></Protected>} />
              <Route path="/academy" element={<Protected section="academy"><Academy /></Protected>} />
              <Route path="/owner-portal" element={<Protected section="owner_portal"><OwnerPortal /></Protected>} />
              <Route path="/reports" element={<Protected section="reports"><Reports /></Protected>} />
              <Route path="/users" element={<Protected section="users"><UsersAdmin /></Protected>} />
              <Route path="/settings" element={<Protected section="settings"><Settings /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
